import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Container, useTick } from '@pixi/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ACTIVE_TIMER_GREEN_COLOR_NUM, ACTIVE_TIMER_GREEN_HIGHLIGHT_COLOR_NUM, ACTIVE_TIMER_RED_COLOR_NUM, ACTIVE_TIMER_RED_HIGHLIGHT_COLOR_NUM, ACTIVE_TIMER_YELLOW_COLOR_NUM, ACTIVE_TIMER_YELLOW_HIGHLIGHT_COLOR_NUM, DARK_GREENISH_BLUE_NUM, DARKEST_GREENISH_BLUE_NUM } from '../../../styles/colors';
import { getRemainingTimeRatio } from '../../../utils/animationUtils';
import { isTestEnvironment } from '../../../utils/utils';
import { Rect } from './rect';
const COLORED_BAR_PADDING_PX = 1;
const TIMER_BAR_COLOR_PROPERTIES = {
    green: {
        fillColor: ACTIVE_TIMER_GREEN_COLOR_NUM,
        highlightFillColor: ACTIVE_TIMER_GREEN_HIGHLIGHT_COLOR_NUM
    },
    yellow: {
        fillColor: ACTIVE_TIMER_YELLOW_COLOR_NUM,
        highlightFillColor: ACTIVE_TIMER_YELLOW_HIGHLIGHT_COLOR_NUM
    },
    red: {
        fillColor: ACTIVE_TIMER_RED_COLOR_NUM,
        highlightFillColor: ACTIVE_TIMER_RED_HIGHLIGHT_COLOR_NUM
    }
};
export const ActiveTimerBar = ({ startTimestampMs, totalMs, x, y, w, h, halfRemainingCallback, quarterRemainingCallback, pixiData }) => {
    const remainingRatio = useRef(getRemainingTimeRatio(startTimestampMs, totalMs));
    const [timerBarColor, setTimerBarColor] = useState('green');
    const rectRef = useRef();
    const rectHighlightRef = useRef();
    const maxColoredBarWidth = w - 2 * COLORED_BAR_PADDING_PX;
    const rectRefFn = useCallback((rect) => {
        rectRef.current = rect;
    }, []);
    const rectHighlightRefFn = useCallback((rect) => {
        rectHighlightRef.current = rect;
    }, []);
    const animate = useCallback(() => {
        if (!rectRef.current) {
            return;
        }
        if (!rectHighlightRef.current) {
            return;
        }
        const newRemainingRatio = getRemainingTimeRatio(startTimestampMs, totalMs);
        const newWidth = newRemainingRatio * maxColoredBarWidth;
        rectRef.current.width = newWidth;
        rectHighlightRef.current.width = newWidth;
        // TODO: Color the bar on room entry if it is below 50%.
        if (newRemainingRatio <= 0.25) {
            if (timerBarColor !== 'red') {
                // gone into red territory
                setTimerBarColor('red');
                quarterRemainingCallback?.();
            }
        }
        else if (newRemainingRatio <= 0.5) {
            if (timerBarColor !== 'yellow') {
                // gone into yellow territory
                setTimerBarColor('yellow');
                halfRemainingCallback?.();
            }
        }
        remainingRatio.current = newRemainingRatio;
    }, [startTimestampMs, totalMs, maxColoredBarWidth, timerBarColor, quarterRemainingCallback, halfRemainingCallback]);
    // if startTimestampMs changes, it's a new player action, so ensure color is reset to green:
    useEffect(() => {
        setTimerBarColor('green');
    }, [startTimestampMs]);
    useTick(animate);
    const { fillColor, highlightFillColor } = TIMER_BAR_COLOR_PROPERTIES[timerBarColor];
    const coloredBarH = h - COLORED_BAR_PADDING_PX * 2;
    return (_jsxs(Container, { interactiveChildren: false, x: x, y: y, "pixi-data": isTestEnvironment() ? pixiData : null, children: [_jsx(Rect, { x: 0, y: 0, w: w, h: h, fillColor: DARK_GREENISH_BLUE_NUM }), _jsxs(Container, { x: COLORED_BAR_PADDING_PX, y: COLORED_BAR_PADDING_PX, children: [_jsx(Rect, { ref: rectRefFn, x: 0, y: 0, w: maxColoredBarWidth, h: coloredBarH, fillColor: fillColor }), _jsx(Rect, { ref: rectHighlightRefFn, x: 0, y: 0, w: maxColoredBarWidth, h: Math.floor(coloredBarH / 2), fillColor: highlightFillColor })] }), _jsx(Rect, { x: -2, y: -2, w: w + 4, h: h + 4, lineColor: DARKEST_GREENISH_BLUE_NUM, radius: 9, lineWidth: 3 })] }));
};
